/**
 * §M2 · Contextual SCREEN → GUIDE link, mounted ONCE in the shell.
 *
 * Previously each screen decided whether to add its "?" — and seventeen did and the
 * rest didn't. Here the link is DERIVED from the active route (`/academy/cohorts` →
 * area `academy.cohorts` ▸ `academy`), so it appears everywhere by
 * construction and there's no list to maintain.
 *
 * Filtered by ROLE and by FLAG, which is the real requirement: documentation for
 * something the user can't use is never offered. If the route corresponds to
 * a nav item their role doesn't see, or a feature their academy turned off, the
 * link isn't rendered — reading how to use a screen you can't enter
 * only generates frustration.
 */
import { createMemo, Show } from "solid-js";
import { useLocation } from "@tanstack/solid-router";

import { NAV_SECTIONS } from "@/features/navigation/nav-model";
import { useNavContext } from "@/features/navigation/use-nav-context";
import { helpAreaForPath } from "@/features/profiles/profile-model";
import { HelpButton } from "./help-button";
import { helpTargetForArea } from "../registry";

/** Help area used to label the button (for the stable testid). */
function areaKeyForPath(path: string): string | undefined {
  const segments = path.split("/").filter((s) => s.length > 0);
  for (let depth = Math.min(segments.length, 2); depth >= 1; depth -= 1) {
    const key = segments.slice(0, depth).join(".");
    if (helpTargetForArea(key)) {
      return key;
    }
  }
  return undefined;
}

/**
 * Can this user USE the screen `path` covers? Answered with the
 * same nav-model that governs the sidebar: if no nav item from that
 * area is visible for their context, the screen isn't theirs.
 *
 * Routes with no nav item of their own (details like `/learn/$courseId`, or
 * `/notifications`) aren't hidden: the user is already inside.
 */
function usableByContext(path: string, ctx: ReturnType<typeof useNavContext>): boolean {
  const root = `/${path.split("/").filter((s) => s.length > 0)[0] ?? ""}`;
  const items = NAV_SECTIONS.flatMap((s) => s.items).filter(
    (item) => item.to === path || item.to.startsWith(`${root}/`) || item.to === root,
  );
  if (items.length === 0) {
    return true;
  }
  const context = ctx();
  return items.some(
    (item) =>
      item.visible(context) && (item.feature === undefined || context.featureEnabled(item.feature)),
  );
}

export function RouteHelpLink() {
  const location = useLocation();
  const navContext = useNavContext();
  const path = createMemo(() => location().pathname);
  const area = createMemo(() => {
    const p = path();
    return helpAreaForPath(p) && usableByContext(p, navContext) ? areaKeyForPath(p) : undefined;
  });

  return (
    <Show when={area()}>
      {(resolved) => (
        <div class="pointer-events-none sticky top-0 z-20 flex h-0 justify-end px-3 pt-3 md:px-5">
          <div class="pointer-events-auto rounded-full bg-background/70 backdrop-blur-sm">
            <HelpButton area={resolved()} />
          </div>
        </div>
      )}
    </Show>
  );
}
